// Constructors that keep the canvas definitions short, and makeDef, which joins a definition to the course spine.
import { SPINE, OPT } from '../spine.js';

// ---------------------------------------------------------------- guide: the inline help for one field
// G(how, example, rule, trap): how = what to write; example = the NorthStar entry or a model sentence;
// rule = the scoring rule or anchor; trap = the common mistake. Any can be omitted with undefined.
export const G = (how, example, rule, trap) => {
  const g = { how, example, rule, trap };
  Object.keys(g).forEach((k) => g[k] === undefined && delete g[k]);
  return g;
};

// ---------------------------------------------------------------- fields (used standalone in a fields block, or as a table column)
// options for choice and multi: ['a', 'b'] or [{ v: '1', label: '1', hint: 'Ad hoc' }]
// common props: required (true, or a function ({ v, up, d, row }) => bool), placeholder, help (a short grey line under the label),
//   guide (G(...)), w (grid span 1 to 4 on a 4-column grid; default 2 for text, 1 for choices), unit / prefix (for numbers), rows (long text height)
export const text = (id, label, o = {}) => ({ id, label, kind: 'text', ...o });
export const long = (id, label, o = {}) => ({ id, label, kind: 'long', ...o });
export const num = (id, label, o = {}) => ({ id, label, kind: 'number', ...o });
export const pick = (id, label, options, o = {}) => ({ id, label, kind: 'choice', options, ...o });
export const multi = (id, label, options, o = {}) => ({ id, label, kind: 'multi', options, ...o });
export const tag = (id = 'tag', label = 'Tag', o = {}) => ({ id, label, kind: 'tag', options: OPT.tag, ...o });
// calc: never typed. Its value is derive().calc[key] (key = id in a fields block; table.row.id in a table). `rule` is the plain-words rule.
export const calc = (id, label, o = {}) => ({ id, label, kind: 'calc', ...o });

// ---------------------------------------------------------------- blocks (a step holds blocks)
// fields: a grid of single fields
export const fields = (list, o = {}) => ({ type: 'fields', fields: list, ...o });
// table: rows of the same columns. id is the key prefix.
//   rows: { min, max, start, label } user-managed | { fixed: [{ id, label, sub }] } | { from: (up, v) => [{ id, label, sub }] }
//   pdf: 'table' (a true table in the PDF, for short cells) or 'cards' (one small card per row)
export const table = (id, o) => ({ type: 'table', id, ...o });
// pull: read-only values pulled from earlier canvases. items: [{ id, label, ref: 'Case Card B', no: 0, get: (up, v) => string|null }]
// `no` is the canvas the value comes from (the link says "Change on Canvas n"). get returns null while the source is empty.
export const pull = (id, title, items, o = {}) => ({ type: 'pull', id, title, items, ...o });
// note: static words. tone: 'info' (default), 'rule' (a rule to remember), 'warn'
export const note = (textStr, tone = 'info') => ({ type: 'note', text: textStr, tone });
// reference: a small read-only table (rubric, anchors, key) in a collapsible "Reference" panel. rows: array of arrays of strings.
export const reference = (title, head, rows, o = {}) => ({ type: 'reference', title, head, rows, ...o });

// ---------------------------------------------------------------- makeDef
// no: canvas number. rest: { purpose, howTo, steps, results, signoff, derive, NORTHSTAR, ... }
// steps: [{ id?, title?, minutes?, hint, blocks: [...] }] in the printed fill order; title and minutes default to the spine's.
export function makeDef(no, rest) {
  const sp = SPINE[no];
  const steps = rest.steps.map((st, i) => ({
    id: `s${i + 1}`,
    title: sp.steps[i] ? sp.steps[i].title : undefined,
    minutes: sp.steps[i] ? sp.steps[i].minutes : undefined,
    ...st,
  }));
  return {
    no,
    title: sp.name,
    question: sp.question,
    sceptic: sp.sceptic,
    module: sp.module,
    arc: sp.arc,
    day: sp.day,
    minutes: sp.steps.reduce((a, s) => a + s.minutes, 0),
    pullsFrom: sp.pulls.filter((p) => p.no !== undefined).map((p) => p.no),
    feeds: sp.feeds.filter((p) => p.no !== undefined).map((p) => p.no),
    signoffQuestions: sp.sceptic_questions,
    signoff: no >= 1 && no <= 8 ? { mode: 'ledger' } : undefined,
    results: [],
    ...rest,
    steps,
  };
}

export { OPT };
